import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";
import { api } from "../App";

const AffiliateThankYou = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isActiveAffiliate, setIsActiveAffiliate] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);

  // On mount: check affiliate status
  useEffect(() => {
    const checkAffiliateStatus = async () => {
      try {
        const res = await api.get("/affiliates/me");
        
        if (res.data?.is_affiliate === true && res.data?.status === "active") {
          setIsActiveAffiliate(true);
          setNeedsLogin(false);
        } else {
          // Authenticated but not active affiliate
          setIsActiveAffiliate(false);
          setNeedsLogin(false);
        }
      } catch (err) {
        // 401 means needs login
        if (err.response?.status === 401 || err.response?.data?.needs_login) {
          setNeedsLogin(true);
          setIsActiveAffiliate(false);
        }
      } finally {
        setLoading(false);
      }
    };

    checkAffiliateStatus();
  }, []);

  // Handle sign in and redirect to dashboard
  const handleSignIn = () => {
    // Store returnTo so after auth, user goes to affiliate dashboard
    sessionStorage.setItem('returnTo', '/affiliates/dashboard');
    // Redirect to Emergent Auth
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Handle go to dashboard
  const handleGoToDashboard = () => {
    navigate('/affiliates/dashboard');
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Logo showText linkTo="/" />
        </div>
      </nav>

      <main className="pt-28 pb-16 px-6 md:px-12 flex items-center justify-center min-h-[80vh]">
        <div className="max-w-lg mx-auto text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 rounded-full bg-[#D4F34A]/10 flex items-center justify-center mx-auto mb-8">
            <CheckCircle2 className="w-10 h-10 text-[#D4F34A]" />
          </div>

          {/* Heading */}
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
            You&apos;re in 🎉
          </h1>

          {/* Main text */}
          <p className="text-text-muted text-lg mb-4">
            Your affiliate account is now active!
          </p>
          <p className="text-text-muted mb-8">
            Check your email for your unique referral link and start earning immediately.
          </p>

          {/* What happens next */}
          <div className="bg-surface border border-[#262626] rounded-xl p-6 mb-8 text-left">
            <h3 className="font-semibold text-white mb-4">You&apos;re ready to earn</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-text-muted">
                <CheckCircle2 className="w-5 h-5 text-[#D4F34A] flex-shrink-0" />
                <span>Your referral link has been emailed to you</span>
              </li>
              <li className="flex items-center gap-3 text-text-muted">
                <CheckCircle2 className="w-5 h-5 text-[#D4F34A] flex-shrink-0" />
                <span>Share your link with anyone who could benefit</span>
              </li>
              <li className="flex items-center gap-3 text-text-muted">
                <CheckCircle2 className="w-5 h-5 text-[#D4F34A] flex-shrink-0" />
                <span>Earn 30% recurring commission on every payment</span>
              </li>
            </ul>
          </div>

          {/* Action nudge */}
          <div className="bg-[#D4F34A]/5 border border-[#D4F34A]/20 rounded-lg p-4 mb-8">
            <p className="text-sm text-text-muted">
              <span className="text-white font-medium">Pro tip:</span> Think of 3 people who could use this and send them your link today.
            </p>
          </div>

          {/* Dynamic CTA based on affiliate status */}
          {loading ? (
            <Button 
              className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold px-6" 
              disabled
            >
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Loading...
            </Button>
          ) : isActiveAffiliate ? (
            <Button 
              onClick={handleGoToDashboard}
              className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold px-6" 
              data-testid="goto-dashboard-btn"
            >
              Go to Affiliate Dashboard <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : needsLogin ? (
            <Button 
              onClick={handleSignIn}
              className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold px-6" 
              data-testid="signin-dashboard-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in to access dashboard
            </Button>
          ) : (
            // Authenticated but pending verification or other status
            <Button 
              onClick={handleSignIn}
              className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold px-6" 
              data-testid="verify-dashboard-btn"
            >
              Verify & Access Dashboard <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}

          {/* Secondary link to home */}
          <Link 
            to="/" 
            className="block mt-4 text-sm text-text-muted hover:text-white transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#262626]">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-text-muted text-sm">
            Questions?{" "}
            <a href="mailto:hello@replydriven.com" className="text-[#D4F34A] hover:underline">
              hello@replydriven.com
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AffiliateThankYou;
